import { Fragment, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ChevronDown, AlertTriangle, FileText, Search, X, RotateCcw, MoreHorizontal, Settings } from '../../icons';
import {
  listMyTasks, patchTask, deleteTask,
  // 완료 승인 (2026-09-04)
  submitTaskForApproval, approveTask, rejectTask, listPendingApprovals,
  type MyTaskDto, type MyTasksDueFilter, type TaskStatus, type PendingApprovalDto,
} from '../../api/tasks';
import { listChecklist, listTaskGroups, type TaskGroupDto } from '../../api/task-extras';
import { Combobox, Skeleton } from '../../components/shared';
import { layerAlert, layerConfirm, layerPrompt } from '../../components/LayerPopup/LayerPopup';
import { DetailPageHeader } from '../ProjectPage/DetailPageHeader';
// 2026-09-16 — 좌측 프로젝트 네비를 이 화면에도 (사용자 지시). 아래 트리는 그대로 두고 감싸기만 한다.
import { ProjectNavShell } from '../ProjectPage/ProjectNavShell';
import { TaskGroupCreateModal } from './TaskGroupCreateModal';
// 2026-09-03 — 만든 그룹의 이름 바꾸기·지우기. 종전에는 만들기만 있었다.
import { TaskGroupManageModal } from './TaskGroupManageModal';
import { MyTaskCreateModal } from './MyTaskCreateModal';
import { MyTaskDetailPanel } from './MyTaskDetailPanel';
// 2026-09-04 — [📅] 가 하루 미루기에서 날짜 고르기로. 신규 파일.
import { MyTaskDuePicker, type DuePickerTarget } from './MyTaskDuePicker';
import { dueState, dueStateLabel, isAtRisk } from '../ProjectPage/my-task-due';
// 2026-08-26 — 요약(완료율 · 상태 분포 · 마감 흐름 · 프로젝트 쏠림)을 한 서랍으로. 신규 파일 — 목록 로직 무수정.
import { MyTasksSummaryToggle, MyTasksSummaryPanel, useSummaryOpen } from './MyTasksSummary';
// 2026-08-27 — 남는 폭을 채우는 것들(그룹 요약 · 남은 기간 막대 · 행 인라인 액션 · 태그 열).
// 전부 표시 전용 신규 파일이고, 값을 바꾸는 것은 RowActions 뿐인데 그것도 여기서 준 콜백을 부른다.
import {
  GroupSummary, RowActions, TagCell, shouldShowTagColumn,
} from '../ProjectPage/MyTasksRowExtras';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import '../ProjectPage/ProjectPage.css';
import './MyTasksBoard.css';
// 2026-08-27 — 공백 회수(상단 1줄 · 표+요약 2단 · 열 폭 고정 · 행 밀도). 기존 규칙을 덮는 추가 블록이라
// 반드시 MyTasksBoard.css 뒤에 온다. 되돌리려면 이 import 와 아래 래퍼 두 개만 지우면 된다.
import './MyTasksDense.css';
// 완료 승인 (2026-09-04) — 승인 대기함·배지·버튼 스타일. 기존 CSS 는 건드리지 않는다.
import './TaskApproval.css';

/**
 * 한 번에 받아 오는 내 업무 수 — 그룹 묶기·정렬을 클라이언트가 하므로 한 번에 받는다.
 * 서버의 `/tasks/me` 는 limit 상한이 100 이라 그 위로 올리면 400 (querystring/limit must be <= 100).
 */
const FETCH_LIMIT = 100;
// 개인 프로젝트 이름('내 할일')은 [업무 생성] 이 모달로 바뀌면서 MyTaskCreateModal 로 옮겨 갔다
// (여기 남아 있던 상수는 quickAdd 가 사라진 뒤로 아무도 쓰지 않아 typecheck 를 깨뜨렸다).

/**
 * 상태 칩 (2026-08-25 단순화) — 종전 5종(전체·진행중·완료·지연·위험) → 3종.
 *   - '완료' 는 칩이 아니라 목록 끝의 '완료 n건 보기' 로 간다(대부분의 행이 이미 끝난 일이라
 *     기본 목록의 절반을 차지하고 있었다).
 *   - '위험' 은 **지연의 부분집합**(지연 + 우선순위 높음)이라 칩으로 또 세면 같은 일을 두 번
 *     세는 셈이 된다. 행 안의 ⚠ 표시로 이미 구분된다.
 */
type Chip = 'ALL' | 'IN_PROGRESS' | 'OVERDUE';

/**
 * AW-005 (2026-08-25) — 프로젝트 홈 히어로 숫자의 착지점.
 *
 * `/project/my-tasks?filter=dueSoon|overdue|risk` 로 들어오면 그 조건만 받아 온다.
 * 종전에는 홈에서 "지연 9" 를 보고도 그 9건으로 갈 방법이 없었다.
 *
 * 왜 칩(client 필터)이 아니라 **서버 필터**인가: 이 화면은 `/tasks/me` 를 100건씩 받아
 * 칩으로 걸러 세는데, 100건을 넘는 사람은 칩 숫자가 받아 온 만큼만 센 값이 된다. 홈의
 * 숫자는 서버 전체 집계라 둘이 어긋난다. 홈에서 넘어온 필터만큼은 서버가 걸러 주면
 * 목록·총건수가 홈에서 본 숫자와 정확히 같아진다.
 */
const URL_DUE_FILTER: Record<string, MyTasksDueFilter> = {
  dueSoon: 'soon',
  overdue: 'overdue',
  risk: 'risk',
};
const DUE_FILTER_LABEL: Record<MyTasksDueFilter, 'project.mytasks.filter_due_soon' | 'project.mytasks.filter_overdue' | 'project.mytasks.filter_risk'> = {
  soon: 'project.mytasks.filter_due_soon',
  overdue: 'project.mytasks.filter_overdue',
  risk: 'project.mytasks.filter_risk',
};
type SortKey = 'due' | 'priority' | 'recent';

const PRIORITY_RANK: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };

/** 되돌리기 줄이 떠 있는 시간(ms) — 누른 직후 "어" 하고 손이 가는 만큼만. */
const UNDO_MS = 7000;
/** 목록을 아직 못 받았을 때 자리를 잡아 두는 스켈레톤 행 수. */
const SKELETON_ROWS = 5;

/**
 * MyTasksBoard (AW-004-01, 2026-08-18) — **내 업무** 목록.
 *
 * 설계서가 지적한 두 가지를 고친다:
 *   ① 내 할일이 조회 전용이라 할 수 있는 게 없다 → 완료 체크·그룹 묶기·직접 추가·상세 패널.
 *   ② 항목을 눌러도 프로젝트로만 튕겨 무슨 내용인지 모른다 → 그 자리에서 상세(AW-004-03).
 *
 * 색은 상태·위험도에만 쓴다(설계서 2.1): 완료=초록 / 진행중=파랑 / 지연·임박=주황 / 위험=빨강.
 * 우선순위는 상태색과 겹치지 않게 인디고 명도 차이로만 구분하고, 지연 행을 통째로 물들이지 않는다.
 *
 * 기존 `MyTasksDetailPage` 는 보존한다(소스 회귀 금지) — 라우트만 이 화면으로 바꿔 끼웠다.
 */
export function MyTasksBoard() {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const navigate = useNavigate();

  const [items, setItems] = useState<MyTaskDto[]>([]);
  const [groups, setGroups] = useState<TaskGroupDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chip, setChip] = useState<Chip>('ALL');
  // AW-005 — 홈에서 넘어온 기한 필터. URL 이 진실이라 새로고침·뒤로가기에도 유지된다.
  const [searchParams, setSearchParams] = useSearchParams();
  const dueFilter = URL_DUE_FILTER[searchParams.get('filter') ?? ''] ?? null;
  const [sort, setSort] = useState<SortKey>('due');
  const [groupFilter, setGroupFilter] = useState<string>('');       // '' = 전체 그룹
  const [groupModal, setGroupModal] = useState(false);
  /** 2026-09-03 — 그룹 관리(이름·삭제) 모달. 등록 모달과 별개로 연다. */
  const [groupManage, setGroupManage] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  // 2026-08-20 — [업무 생성] 은 모달을 연다(표 아래 한 줄 입력 대체).
  const [createOpen, setCreateOpen] = useState(false);
  // 2026-08-24 — 제목·프로젝트로 찾기. 서버가 아니라 이미 받아 둔 목록에서 거른다.
  const [q, setQ] = useState('');
  /** 2026-08-25 — 완료 목록 펼침. 영속하지 않는다(들어올 때마다 '할 일' 이 먼저 보이게). */
  const [showDone, setShowDone] = useState(false);
  /** 2026-08-25 — 그룹 추가·체크리스트 템플릿을 담은 ⋯ 메뉴. 매일 쓰는 것이 아니라 접어 둔다. */
  const [toolsMenu, setToolsMenu] = useState(false);
  /** 2026-08-26 — 요약(도넛·상태분포·차트) 펼침. 기본 접힘이라 들어오면 목록이 먼저 보인다. */
  const [summaryOpen, toggleSummary] = useSummaryOpen();
  /*
   * 완료 승인 (2026-09-04) — 내가 승인해야 할 것들.
   *
   * 승인권이 없거나 대기 중인 것이 없으면 배열이 비고, 그때는 승인함 자체를 그리지 않는다
   * — 승인을 안 쓰는 조직의 화면은 종전과 한 픽셀도 다르지 않다.
   * 기본은 **펼침**이다: 요약·완료 서랍과 달리 이것은 '내가 아직 안 한 일' 이라 접어 두면
   * 그대로 잊힌다(그 둘은 이미 지나간 것을 보는 서랍이라 접는 편이 맞았다).
   */
  const [pending, setPending] = useState<PendingApprovalDto[]>([]);
  const [pendingOpen, setPendingOpen] = useState(true);
  /** 지금 승인/반려 처리 중인 업무 id — 두 번 눌리는 것을 막는다. */
  const [apBusyId, setApBusyId] = useState<string | null>(null);
  /**
   * 기한 고르기 팝오버 (2026-09-04) — 열려 있는 대상. null 이면 닫힘.
   * 한 번에 하나만 연다 — 행마다 열리면 어느 업무의 기한을 고치는지 흐려진다.
   */
  const [duePick, setDuePick] = useState<DuePickerTarget | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  /**
   * 서버가 세는 전체 건수. `/tasks/me` 는 limit 상한이 100 이라 한 번에 다 못 받는데,
   * 종전에는 `total` 을 버리고 100건만 그려서 **나머지가 있다는 사실 자체가 안 보였다**.
   */
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  /** 방금 바꾼 완료 상태 — 잘못 눌렀을 때 한 번에 되돌리라고 띄운다. */
  const [undo, setUndo] = useState<{ id: string; title: string; prev: TaskStatus; next: TaskStatus } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, gs, ap] = await Promise.all([
        listMyTasks({ limit: FETCH_LIMIT, offset: 0, ...(dueFilter ? { due: dueFilter } : {}) }),
        listTaskGroups().catch(() => [] as TaskGroupDto[]),
        /*
         * 완료 승인 (2026-09-04) — 내가 승인할 것들. 승인자는 그 업무의 담당자가 아니라서
         * '내 업무' 목록에는 안 뜬다. 이 조회가 없으면 결재를 기다리는 업무를 찾을 길이 없다.
         * 실패해도 목록은 그대로 보여야 하므로 빈 배열로 흡수한다(승인함만 안 뜬다).
         */
        listPendingApprovals({ limit: 50 }).catch(() => ({ items: [] as PendingApprovalDto[], total: 0 })),
      ]);
      setItems(res.items);
      setTotal(res.total ?? res.items.length);
      setGroups(gs);
      setPending(ap.items);
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error).message ?? tProject('project.mytasks.err_fetch'));
    } finally {
      setLoading(false);
    }
  }, [dueFilter]);

  /** [더 보기] — 이미 받은 뒤부터 이어 받아 **덧붙인다**(다시 처음부터 받지 않는다). */
  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    setError(null);
    try {
      const res = await listMyTasks({
        limit: FETCH_LIMIT, offset: items.length, ...(dueFilter ? { due: dueFilter } : {}),
      });
      // 그 사이 앞쪽이 바뀌어 같은 업무가 또 와도 목록에 두 번 그리지 않는다.
      setItems((cur) => {
        const seen = new Set(cur.map((t) => t.id));
        return [...cur, ...res.items.filter((t) => !seen.has(t.id))];
      });
      setTotal(res.total ?? total);
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error).message ?? tProject('project.mytask.err_more'));
    } finally {
      setLoadingMore(false);
    }
  }, [items.length, total, dueFilter]);

  useEffect(() => { void load(); }, [load]);

  // ⋯ 메뉴는 바깥을 누르거나 Esc 로 닫는다 — 열어 둔 채 다른 걸 누르면 방해가 된다.
  useEffect(() => {
    if (!toolsMenu) return undefined;
    function close(e: MouseEvent | KeyboardEvent) {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return;
      setToolsMenu(false);
    }
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [toolsMenu]);

  // 되돌리기 줄은 잠깐만 둔다 — 다음 완료를 누르면 그 업무 것으로 갈린다.
  useEffect(() => {
    if (!undo) return;
    const timer = window.setTimeout(() => setUndo(null), UNDO_MS);
    return () => window.clearTimeout(timer);
  }, [undo]);

  // 태스크 → 내 그룹 (없으면 null). 그룹은 taskIds 를 들고 오므로 역인덱스를 한 번만 만든다.
  const groupOfTask = useMemo(() => {
    const m: Record<string, TaskGroupDto> = {};
    for (const g of groups) for (const tid of g.taskIds) m[tid] = g;
    return m;
  }, [groups]);

  const counts = useMemo(() => {
    let inProgress = 0; let done = 0; let overdue = 0;
    for (const t of items) {
      if (t.status === 'IN_PROGRESS') inProgress += 1;
      if (t.status === 'DONE') done += 1;
      if (dueState(t.dueDate, t.status) === 'overdue') overdue += 1;
    }
    // open = 아직 안 끝난 일. 칩은 **눈앞의 목록**을 세고, 위 히어로가 전체(완료 포함)를 센다.
    return { total: items.length, open: items.length - done, inProgress, done, overdue };
  }, [items]);

  const pct = counts.total > 0 ? Math.round((counts.done / counts.total) * 100) : 0;

  /** 검색·칩·그룹 중 하나라도 걸려 있으면, 빈 목록이 "할 게 없다" 가 아니라 "안 걸렸다" 다. */
  const filtered = chip !== 'ALL' || groupFilter !== '' || q.trim() !== '';

  const clearFilters = useCallback(() => {
    setChip('ALL');
    setGroupFilter('');
    setQ('');
  }, []);

  const visible = useMemo(() => {
    let list = items;
    if (chip === 'IN_PROGRESS') list = list.filter((t) => t.status === 'IN_PROGRESS');
    else if (chip === 'OVERDUE') list = list.filter((t) => dueState(t.dueDate, t.status) === 'overdue');
    if (groupFilter) list = list.filter((t) => groupOfTask[t.id]?.id === groupFilter);
    // 제목과 프로젝트 이름 둘 다 본다 — "그 프로젝트 것만" 이 흔한 찾기라서.
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((t) => t.title.toLowerCase().includes(needle)
        || (t.project?.name ?? '').toLowerCase().includes(needle));
    }

    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sort === 'priority') {
        const d = (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
        if (d !== 0) return d;
      }
      if (sort === 'recent') return a.id < b.id ? 1 : -1;
      // 마감임박순 — 마감일 없는 업무는 뒤로.
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0;
    });
    return sorted;
  }, [items, chip, sort, groupFilter, groupOfTask, q]);

  /**
   * 완료된 업무는 기본 목록에서 접는다 (2026-08-25).
   *
   * 로컬 dev 데이터에서 30행 중 14행이 취소선 그어진 완료 행이었다. "오늘 뭘 하지" 를 보러
   * 온 화면인데 절반이 이미 끝난 일이라, 스크롤도 시선도 두 배로 든다. 목록 끝의
   * '완료 n건 보기' 로 옮기고, 펼치면 그 자리에 이어 붙인다.
   * 펼침 상태는 남기지 않는다 — 다음에 들어올 때도 '할 일' 이 먼저 보이는 편이 맞다.
   */
  const openRows = useMemo(() => visible.filter((t) => t.status !== 'DONE'), [visible]);
  const doneRows = useMemo(() => visible.filter((t) => t.status === 'DONE'), [visible]);

  /**
   * 그룹 헤더로 묶은 목록 — 그룹 없는 업무는 '미분류' 로 맨 뒤에 둔다.
   *
   * ## ⭐⭐ 2026-10-10 — ★ 「⭐ 업무가 0 인 그룹도 ★ 칸으로 보인다」 (⇒ 사용자 결정 ⓑ)
   *
   * ⛔ 종전에는 ★ 이 지도를 ⇒ ⭐ `openRows` «만»으로 채웠다 ⇒ ⇒ ★ 업무가 0 인 그룹은
   *   ⇒ ⇒ ⭐ 지도에 ★ 아예 안 들어가 ⇒ ⇒ ★ ⭐ «만들었는데 안 보였다».
   *   ⇒ ⚠ 그것은 ★ 「이식 누락」이 ⭐ 아니다 — ⇒ ★ 정본(dd05a6fcd)의 이 줄도 ⭐ «글자까지 같다».
   *     ⇒ ⇒ ★ 즉 ⭐ 정본도 ★ 안 보인다. ⇒ ⇒ ⭐ 그래서 ★ 이 판은 ⭐ «정본에서 벗어나는» 판이다
   *       ⇒ ⇒ ⇒ ★ 사용자가 ⭐ 그렇게 골랐다(2026-10-10 · 관제 경유 · 원문 「b」).
   *
   * ## ⛔⛔ 그런데 ★ 「미분류」는 ⇒ ⭐ «되살리지 않는다»
   *
   * ⇒ ★ 아래 `useGroupHeaders` 주석이 ⭐ 그 까닭을 적는다 — 「그룹이 하나도 안 붙은 상태에서는
   *   '미분류' 헤더 한 줄이 늘 서 있었는데, ★ 아무것도 나누지 않는 구분선이라 ⭐ 잡음이었다」.
   * ⇒ ⇒ ✅ 그래서 ★ ⭐ «구조로» 지킨다 — ⇒ 아래에서 ⭐ «그룹»만 미리 담고
   *   ⇒ ⇒ ★ `__none__` 은 ⭐ «업무가 있을 때만» 생긴다(⇒ 종전과 같다).
   *   ⇒ ⇒ ⇒ ⭐ 즉 ★ 「빈 그룹을 더한다」와 「미분류를 안 세운다」가 ★ ⭐ 둘 다 참이다.
   *
   * ## ⚠ 거르기가 걸린 동안은 ★ 더하지 ⛔ 않는다
   * ```
   * ⇒ 이 파일 :247 이 ★ 그 가름을 ⭐ 이미 두었다:
   *     const filtered = chip !== 'ALL' || groupFilter !== '' || q.trim() !== '';
   *     「검색·칩·그룹 중 하나라도 걸려 있으면, ★ 빈 목록이 "할 게 없다" 가 아니라 ⭐ "안 걸렸다" 다」
   * ⇒ ⇒ ⭐ 그러므로 ★ 칩·검색으로 좁힌 자리에 ⇒ ⭐ 빈 그룹을 세우면 ★ «안 걸린 것»을
   *   ⇒ ⇒ ★ 「0건인 그룹」으로 ⭐ 읽게 만든다 ⇒ ⇒ ⛔ 그래서 안 더한다.
   * ⇒ ✅ 다만 ★ `groupFilter` 로 ⭐ «그 그룹을 골라» 본 경우는 ★ 더한다 —
   *   ⇒ ⇒ ★ 고른 그룹이 ⭐ 비어 있다는 것은 ⇒ ★ ⭐ 「안 걸렸다」가 아니라 «그 그룹의 사실»이다.
   * ```
   * ⇒ ⭐ 그리고 ★ 서버 호출은 ⭐ «안 늘었다» — ⇒ `groups` 는 :160 에서 ★ 이미 받아 둔 것이다.
   * ⇒ ⚠ 그리는 쪽은 ★ ⭐ 한 글자도 안 고쳤다 — ⇒ 머리글이 이미 `그룹이름 (0)` 을 찍고(:972),
   *   ⇒ ⇒ `GroupSummary` 는 빈 묶음에 ★ `null` 을 돌려주며(그 파일 `if (!rows.length) return null`),
   *   ⇒ ⇒ `rows.map` 은 ★ 아무것도 안 그린다.
   */
  const sections = useMemo(() => {
    const map = new Map<string, { group: TaskGroupDto | null; rows: MyTaskDto[] }>();
    /*
     * ⭐ 그룹을 «먼저» 담는다 — ⇒ 업무가 0 이어도 ★ `rows: []` 로 ⭐ 남는다.
     *   ⇒ ⛔ `__none__`(미분류)은 ★ 여기서 ⭐ 담지 않는다(⇒ 위 머리말의 그 결정).
     */
    if (!filtered || groupFilter) {
      for (const g of groups) {
        if (groupFilter && g.id !== groupFilter) continue;
        map.set(g.id, { group: g, rows: [] });
      }
    }
    for (const t of openRows) {
      const g = groupOfTask[t.id] ?? null;
      const key = g?.id ?? '__none__';
      if (!map.has(key)) map.set(key, { group: g, rows: [] });
      map.get(key)!.rows.push(t);
    }
    const arr = [...map.values()];
    arr.sort((a, b) => {
      if (!a.group) return 1;
      if (!b.group) return -1;
      return a.group.sortOrder - b.group.sortOrder;
    });
    return arr;
  }, [openRows, groupOfTask, groups, filtered, groupFilter]);

  const groupOptions = useMemo(() => ([
    { value: '', label: tProject('project.mytask.group_all') },
    ...groups.map((g) => ({ value: g.id, label: g.name })),
  ]), [groups]);

  const sortOptions = useMemo(() => ([
    { value: 'due', label: tProject('project.mytask.sort_due_short') },
    { value: 'priority', label: tProject('project.mytask.sort_priority_short') },
    { value: 'recent', label: tProject('project.mytask.sort_recent_short') },
  ]), []);

  /**
   * 그룹을 실제로 쓸 때만 그룹 헤더를 그린다 (2026-08-25).
   * 그룹이 하나도 안 붙은 상태에서는 '미분류' 헤더 한 줄이 목록 맨 위에 늘 서 있었는데,
   * 아무것도 나누지 않는 구분선이라 정보가 아니라 잡음이었다.
   */
  const useGroupHeaders = sections.some((sec) => sec.group !== null);

  /**
   * 태그 열 (2026-08-27) — 실제로 태그가 붙은 비율이 기준을 넘을 때만 세운다.
   * 무조건 세우면 태그를 안 쓰는 조직에서 통째로 빈 열이 된다(이 화면이 '보통' 배지와
   * '상세' 열을 걷어낸 이유가 정확히 그것이다).
   */
  const tagsOn = useMemo(() => shouldShowTagColumn(items), [items]);
  /** colSpan 은 열 수를 따라간다 — 우선순위 열 포함 6, 태그 열까지 서면 7. */
  const colCount = tagsOn ? 7 : 6;

  /** 행 인라인 액션 — 우선순위 토글. 낙관적 반영 후 실패하면 되돌린다(완료 토글과 같은 방식). */
  async function bumpPriority(t: MyTaskDto) {
    const next = t.priority === 'HIGH' ? 'MEDIUM' : 'HIGH';
    setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, priority: next } : x)));
    try {
      await patchTask(t.id, { priority: next });
    } catch {
      setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, priority: t.priority } : x)));
    }
  }

  /**
   * 행 인라인 액션 — 업무 삭제 (2026-09-01).
   *
   * 확인창을 먼저 띄운다(되돌릴 수 없다). 지워지면 그 줄만 목록에서 빼고 총계를 하나 줄인다
   * — 다시 조회하면 표가 통째로 깜빡이고, 스크롤과 '더 보기' 로 이어 받은 뒤쪽이 날아간다.
   * 열려 있던 상세가 방금 지운 업무면 함께 닫는다.
   *
   * ⚠ 노출 조건을 걸지 않았다 — `/tasks/me` 응답(MyTaskDto)에 `createdBy` 가 없어 카드·상세
   *   패널처럼 '작성자만' 을 화면에서 미리 가릴 수가 없다. 권한은 서버가 그대로 막고
   *   (MANAGER+ 또는 작성자), 거절당하면 아래에서 안내만 한다.
   */
  async function deleteOne(t: MyTaskDto) {
    const ok = await layerConfirm(
      `${tProject('project.mytask.delete_confirm', { title: t.title })}
${tProject('project.mytask.delete_irreversible')}`,
      { danger: true },
    );
    if (!ok) return;
    try {
      await deleteTask(t.id);
      setItems((cur) => cur.filter((x) => x.id !== t.id));
      setTotal((n) => Math.max(n - 1, 0));
      setDetailId((cur) => (cur === t.id ? null : cur));
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      void layerAlert(code === 'FORBIDDEN'
        ? tProject('project.mytask.delete_err_forbidden')
        : tProject('project.mytask.delete_err'));
    }
  }

  /** 행 인라인 액션 — 기한 하루 미루기. 기한이 없으면 오늘부터 하루 뒤로 잡는다. */
  /**
   * 기한 저장 (2026-09-04) — 고른 날짜로 바꾼다. `null` 이면 기한 해제.
   *
   * 종전 postponeOneDay(nextDay = +1일 고정)를 대신한다. 낙관적 반영 → 실패 시 원복이라는
   * 구조는 그대로다(그 부분이 이 화면의 다른 인라인 조작과 같은 규칙이라 바꾸지 않았다).
   * 서버 PATCH 는 dueDate 에 date 문자열과 null 을 둘 다 받는다.
   */
  async function setDueDate(t: MyTaskDto, next: string | null) {
    if (next === (t.dueDate ?? null)) return;   // 같은 값이면 요청하지 않는다
    setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, dueDate: next } : x)));
    try {
      await patchTask(t.id, { dueDate: next });
    } catch {
      setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, dueDate: t.dueDate } : x)));
    }
  }

  async function toggleDone(t: MyTaskDto) {
    // 목록의 동그란 체크로도 완료가 되던 자리 — 체크리스트가 남아 있으면 여기서 끝내지 않고
    // 상세를 연다(2026-08-20). 무엇이 남았는지 보고 정하게 하려는 것이고, 상세의 완료 조건과
    // 목록의 완료 조건이 어긋나 있으면 체크리스트가 다시 무의미해진다.
    if (t.status !== 'DONE') {
      try {
        const cl = await listChecklist(t.id);
        if (cl.some((i) => !i.isDone)) { setDetailId(t.id); return; }
      } catch {
        /* 조회 실패는 막지 않는다 — 종전대로 진행. */
      }
    }
    /*
     * 완료 승인 (2026-09-04) — 승인을 켠 프로젝트에서는 이 체크가 '완료' 가 아니라
     * '완료 요청' 이다. 승인권자(canApproveDone)는 종전대로 바로 완료시킨다 — 그 사람이
     * 승인권자이므로 자기 손으로 두 단계를 밟게 할 이유가 없다.
     *
     * 되돌리기(DONE → TODO)는 이 갈래를 타지 않는다 — 승인은 완료로 가는 문을 지키는
     * 것이지, 이미 끝난 것을 잠그는 것이 아니다(잘못 승인한 것을 못 되돌리면 더 나쁘다).
     */
    if (t.status !== 'DONE' && t.project.requiresDoneApproval === true && t.canApproveDone !== true) {
      setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, status: 'REVIEW' as TaskStatus } : x)));
      try {
        const r = await submitTaskForApproval(t.id);
        setItems((cur) => cur.map((x) => (x.id === t.id
          ? { ...x, status: r.status, rejectReason: r.rejectReason } : x)));
      } catch {
        setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, status: t.status } : x)));
      }
      return;
    }

    const next = t.status === 'DONE' ? 'TODO' : 'DONE';
    setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, status: next } : x))); // optimistic
    try {
      await patchTask(t.id, { status: next });
      // 서버까지 반영된 뒤에만 되돌리기를 연다 — 실패한 것을 되돌리라고 하면 안 되므로.
      setUndo({ id: t.id, title: t.title, prev: t.status, next });
    } catch {
      setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, status: t.status } : x)));
    }
  }

  /**
   * 승인 대기함의 [승인]·[반려] (2026-09-04).
   *
   * 처리한 건은 대기함에서 곧바로 빼고 목록을 다시 읽는다 — 승인한 업무가 대기함에 남아
   * 있으면 '눌렀는데 안 된 것' 으로 읽힌다. 대기함은 줄이지 목록이 아니다.
   */
  async function decide(taskId: string, action: 'approve' | 'reject', reason?: string) {
    setApBusyId(taskId);
    try {
      await (action === 'approve' ? approveTask(taskId) : rejectTask(taskId, reason));
      setPending((cur) => cur.filter((x) => x.id !== taskId));
      await load();
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? tProject('project.mytask.ap_err'));
    } finally {
      setApBusyId(null);
    }
  }

  /** 되돌리기 — 방금 바꾼 상태를 원래대로 되돌린다(체크리스트 확인은 다시 묻지 않는다). */
  async function undoToggle() {
    if (!undo) return;
    const { id, prev } = undo;
    setUndo(null);
    setItems((cur) => cur.map((x) => (x.id === id ? { ...x, status: prev } : x))); // optimistic
    try {
      await patchTask(id, { status: prev });
    } catch {
      setItems((cur) => cur.map((x) => (x.id === id ? { ...x, status: undo.next } : x)));
    }
  }

  const detailTask = detailId ? items.find((t) => t.id === detailId) ?? null : null;

  /** 한 행 — 미완료 섹션과 '완료' 서랍이 같은 모양을 쓰도록 한 곳에 둔다. */
  function renderRow(t: MyTaskDto) {
    const ds = dueState(t.dueDate, t.status);
    const risk = isAtRisk(t.dueDate, t.status, t.priority);
    return (
      <tr className="mtb-row" key={t.id}>
        <td className="mtb-td-title">
          {/* 정렬은 이 래퍼가 한다 — td 자체를 flex 로 만들면 행 구분선에 단차가 생긴다. */}
          <span className="mtb-title-wrap">
            <button
              type="button"
              className={`mtb-check${t.status === 'DONE' ? ' mtb-check--on' : ''}`}
              aria-label={tProject('project.mytask.toggle_aria', { name: t.title })}
              onClick={() => void toggleDone(t)}
            />
            <span className="mtb-titlecol">
              <span className="mtb-titleline">
                <button
                  type="button"
                  className={`mtb-title${t.status === 'DONE' ? ' mtb-title--done' : ''}`}
                  onClick={() => setDetailId(t.id)}
                >
                  {t.title}
                </button>
                {/*
                  완료 승인 (2026-09-04) — 승인을 켠 프로젝트의 업무만 값이 찬다.
                  '검토' 상태 배지(진행 상태 열)와 다른 것을 말한다: 저쪽은 상태 이름이고
                  이쪽은 '내가 올렸고 아직 안 돌아왔다' 는 뜻이라, 제목 옆이 맞다.
                */}
                {t.project.requiresDoneApproval === true && t.status === 'REVIEW' && (
                  <span
                    className="mtb-ap mtb-ap--pending"
                    aria-label={tProject('project.mytask.ap_pending_aria', { name: t.title })}
                  >
                    {tProject('project.mytask.ap_pending')}
                  </span>
                )}
                {t.project.requiresDoneApproval === true && t.rejectReason && t.status !== 'DONE' && t.status !== 'REVIEW' && (
                  <span className="mtb-ap mtb-ap--rejected">
                    {tProject('project.mytask.ap_rejected')}
                  </span>
                )}
                {/*
                  제목 오른쪽 빈 자리 (2026-08-27) — 행에 손이 닿을 때만 드러난다.
                  완료는 넣지 않는다: 행 왼쪽 동그란 체크가 이미 그 일을 한다.
                */}
                <RowActions
                  task={t}
                  onPriority={(x) => void bumpPriority(x)}
                  onPostpone={(x, anchor) => {
                    // 좌표가 없으면(키보드 등 예외 경로) 행을 기준으로 세운다.
                    const rect = anchor ?? new DOMRect(window.innerWidth / 2, 120, 0, 0);
                    setDuePick({ id: x.id, title: x.title, dueDate: x.dueDate, anchor: rect });
                  }}
                  onOpen={(x) => setDetailId(x.id)}
                  onDelete={(x) => void deleteOne(x)}
                />
              </span>
              {/*
                진행률 막대 (2026-08-25) — 값이 있을 때만 그린다. 0%(기본값)까지 그리면
                모든 행에 빈 막대가 깔려, 직전에 걷어낸 '보통' 배지와 같은 잡음이 된다.
                완료 행도 그리지 않는다 — 취소선이 이미 끝났다고 말한다.
              */}
              {/* 왜 돌아왔는지는 목록에서 바로 읽혀야 한다 — 상세를 열어야 알 수 있으면 늦다. */}
              {t.project.requiresDoneApproval === true && t.rejectReason && t.status !== 'DONE' && t.status !== 'REVIEW' && (
                <span className="mtb-ap-reason" title={t.rejectReason}>
                  {tProject('project.mytask.ap_rejected_reason', { reason: t.rejectReason })}
                </span>
              )}
              {t.status !== 'DONE' && (t.progress ?? 0) > 0 && (
                <span
                  className="mtb-progress"
                  role="img"
                  aria-label={tProject('project.mytask.progress_aria', { n: String(t.progress) })}
                >
                  <span className="mtb-progressfill" style={{ width: `${Math.min(t.progress!, 100)}%` }} />
                </span>
              )}
            </span>
          </span>
        </td>
        {/* 우선순위 — '보통' 도 그린다(위 thead 주석 참조). 배지 생김새는 종전 인라인과 같은 클래스. */}
        <td className="mtb-td-pri">
          <span className={`mtb-pri mtb-pri--${t.priority.toLowerCase()}`}>
            {tProject(`project.mytask.pri_${t.priority.toLowerCase()}` as never)}
          </span>
        </td>
        {/*
          상태·진행률 열 (2026-08-27) — 1770px 화면에서 세 열이 각자 폭의 3분의 1씩 잡고
          내용은 왼쪽에 몰려, 열마다 300~500px 가 비어 있었다. 폭을 실제로 쓰는 방법은
          채우는 것뿐이라 **늘 값이 있는 두 가지**만 열로 세운다(우선순위처럼 대부분
          비는 값은 세우지 않는다 — 2026-08-26 에 걷어낸 이유 그대로).
        */}
        <td className="mtb-td-status">
          <span className={`mtb-st mtb-st--${t.status.toLowerCase()}`}>
            {tProject(`project.status.${t.status}` as never)}
          </span>
        </td>
        <td className="mtb-td-prog">
          {t.status === 'DONE' ? (
            <span className="mtb-dash">-</span>
          ) : (
            <span className="mtb-progcell">
              <span
                className="mtb-progress mtb-progress--col"
                role="img"
                aria-label={tProject('project.mytask.progress_aria', { n: String(t.progress ?? 0) })}
              >
                <span className="mtb-progressfill" style={{ width: `${Math.min(t.progress ?? 0, 100)}%` }} />
              </span>
              <b className="mtb-progpct">{t.progress ?? 0}%</b>
            </span>
          )}
        </td>
        {/* 열이 하나 늘어 nth-child 로 잡던 오른쪽 정렬이 어긋난다 — 클래스로 못 박는다. */}
        <td className="mtb-td-due">
          {ds ? (
            /* 한 줄로 (2026-08-25) — 종전에는 '27일 지연' 과 '마감일 2026.07.29' 가 두 줄이라
               행 높이를 키우면서도 같은 사실을 두 번 말하고 있었다. */
            <span className="mtb-duecell mtb-duecell--oneline">
              <b className={`mtb-due mtb-due--${risk ? 'risk' : ds}`}>
                {risk && <AlertTriangle size={11} aria-hidden />}
                {dueStateLabel(ds, t.dueDate)}
              </b>
              {t.dueDate && <small>· {t.dueDate.slice(5).replace('-', '.')}</small>}
            </span>
          ) : <span className="mtb-dash">-</span>}
        </td>
        {tagsOn && (
          <td className="mtb-td-tags"><TagCell tags={t.tags} /></td>
        )}
        {/*
          프로젝트 셀에 스페이스를 접두로 (2026-09-16 사용자 지시).
          `teamName` 은 이미 /tasks/me 응답에 실려 오는데 화면엔 프로젝트명만 나와,
          '모니터링 강화' 가 어느 스페이스 일인지 알 수 없었다. 열을 하나 더 세우는 대신
          같은 칸에 붙인다 — 7열에서 열을 더 늘리면 각 열이 120px 아래로 떨어진다.
          무소속(개인) 프로젝트는 접두 없이 이름만 — 붙일 스페이스가 없다.
          좁아지면 **스페이스 쪽이 먼저 줄고**(flex-shrink), 프로젝트 이름은 끝까지 남는다.
          전체 문구는 툴팁으로 — 잘려도 손을 올리면 읽을 수 있다.
        */}
        <td className="mtb-td-project">
          <span
            className="mtb-project"
            data-tip={t.project?.name
              ? (t.project.teamName ? `${t.project.teamName} · ${t.project.name}` : t.project.name)
              : undefined}
          >
            <i className="mtb-projectdot" aria-hidden />
            {t.project?.teamName && (
              <>
                <span className="mtb-pspace">{t.project.teamName}</span>
                <span className="mtb-psep" aria-hidden>·</span>
              </>
            )}
            <span className="mtb-pname">{t.project?.name ?? '-'}</span>
          </span>
        </td>
      </tr>
    );
  }

  return (
    <ProjectNavShell active="my-tasks">
    <div className="pj-detail-page mtb-page">
      <DetailPageHeader title={tProject('project.mytask.heading')} />

      {/* 2026-08-27 — 상단 두 줄을 한 줄로 묶는 래퍼(추가만). 안의 두 블록은 무수정이며
          CSS 가 display:contents + order 로 한 줄에 세운다. */}
      <div className="mtb-bar">
      <div className="mtb-top">
        {/*
          2026-08-26 — 히어로(도넛 + 상태 분포)를 이 줄에서 뺐다.
          늘 떠 있을 만큼 자주 보는 것이 아닌데 첫 줄을 통째로 차지하고 있었다.
          같은 내용은 툴바의 [요약] 서랍 안에 그대로 있고, 가장 자주 읽는
          '완료 n / 전체 m' 만 그 토글 위에 남겼다.
        */}

        {/* AW-005 — 어디서 넘어와 무엇만 보고 있는지 말해 준다. 없으면 "내 업무가 이게 다" 로 읽힌다. */}
        {dueFilter && (
          <div className="mtb-duefilter" role="status">
            <span className="mtb-duefilter-label">
              <AlertTriangle size={12} aria-hidden />
              {tProject(DUE_FILTER_LABEL[dueFilter])}
              {/* 건수는 히어로가 사라진 자리를 대신해 여기 붙인다 — 몇 건짜리 필터인지 없으면 안 보인다. */}
              <b className="mtb-duefilter-n">{tProject('project.mytask.filtered_count', { n: String(counts.total) })}</b>
            </span>
            <button
              type="button"
              className="mtb-duefilter-clear"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('filter');
                setSearchParams(next, { replace: true });
              }}
            >
              <X size={12} aria-hidden />
              {tProject('project.mytasks.filter_clear')}
            </button>
          </div>
        )}

        {/* 칩은 눈앞의 목록(미완료)을 센다 — 완료 포함 전체는 위 히어로가 말한다. */}
        <div className="mtb-chips" role="tablist" aria-label={tProject('project.mytask.filter_aria')}>
          <ChipButton on={chip === 'ALL'} tone="all" onClick={() => setChip('ALL')}
            label={tProject('project.mytask.chip_all')} n={counts.open} />
          <ChipButton on={chip === 'IN_PROGRESS'} tone="progress" onClick={() => setChip('IN_PROGRESS')}
            label={tProject('project.mytask.chip_progress')} n={counts.inProgress} />
          <ChipButton on={chip === 'OVERDUE'} tone="over" onClick={() => setChip('OVERDUE')}
            label={tProject('project.mytask.chip_overdue')} n={counts.overdue} />
        </div>

        <button type="button" className="mtb-new" onClick={() => setCreateOpen(true)}>
          <Plus size={14} aria-hidden />
          {tProject('project.mytask.new')}
        </button>
      </div>

      <div className="mtb-tools">
        {/* 찾기 — 서버를 다시 부르지 않고 이미 받은 목록에서 거른다(입력 즉시 반영). */}
        <span className="mtb-search">
          <Search size={13} aria-hidden />
          <input
            ref={searchRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tProject('project.mytask.search_ph')}
            aria-label={tProject('project.mytask.search_aria')}
          />
          {q && (
            <button
              type="button"
              className="mtb-search-clear"
              aria-label={tProject('project.mytask.search_clear')} data-tip={tProject('project.mytask.search_clear')}
              onClick={() => { setQ(''); searchRef.current?.focus(); }}
            >
              <X size={12} aria-hidden />
            </button>
          )}
        </span>

        {/* 그룹이 하나도 없으면 좁힐 것도 없다 — 빈 목록만 여는 상자를 세워 두지 않는다. */}
        {groups.length > 0 && (
          <div className="mtb-combo">
            <Combobox
              value={groupFilter}
              onChange={setGroupFilter}
              options={groupOptions}
              ariaLabel={tProject('project.mytask.group_filter_aria')}
            />
          </div>
        )}

        <div className="mtb-combo mtb-combo--sort">
          <Combobox
            value={sort}
            onChange={(v) => setSort(v as SortKey)}
            options={sortOptions}
            ariaLabel={tProject('project.mytask.sort_aria')}
          />
        </div>

        {/*
          요약 서랍 토글 (2026-08-26) — 종전 히어로 + '한눈에 보기' 두 카드가 이 뒤에 들어 있다.
          기한 필터로 들어온 상태에서는 세우지 않는다: 한 갈래만 받아 온 목록이라
          완료율·분포가 언제나 왜곡된 그림이 된다(종전 히어로가 도넛을 숨기던 이유와 같다).
        */}
        {!dueFilter && (
          <MyTasksSummaryToggle
            open={summaryOpen}
            onToggle={toggleSummary}
            done={counts.done}
            total={counts.total}
          />
        )}

        {/*
          그룹 추가·체크리스트 템플릿은 **관리** 쪽 일이라 매일 누르지 않는다.
          늘 첫 줄을 차지하고 있어서, 정작 자주 쓰는 찾기·정렬과 같은 무게로 보였다.
        */}
        <div className="mtb-tools-more">
          <button
            type="button"
            className="mtb-moremenu-btn"
            aria-label={tProject('project.mytask.more_menu')} data-tip={tProject('project.mytask.more_menu')}
            aria-expanded={toolsMenu}
            aria-haspopup="menu"
            onClick={() => setToolsMenu((v) => !v)}
          >
            <MoreHorizontal size={16} aria-hidden />
          </button>
          {toolsMenu && (
            /*
              2026-09-03 — 메뉴 안에서 누른 mousedown 은 document 까지 올려보내지 않는다.
              위 close 핸들러가 **어디를 눌렀는지 안 가리고** 닫아서, 항목을 누르면
              mousedown 에 메뉴가 사라지고 click 이 항목에 닿지 못했다(두 항목 다 죽어 있었다).
              다른 팝오버들(ProfilePopover·Combobox 등)은 ref.contains 로 가리는데 여기만 빠져 있었다.
              닫는 규칙은 그대로 두고(바깥 클릭·Esc), 안쪽 mousedown 만 여기서 멈춘다.
            */
            <div className="mtb-moremenu" role="menu" onMouseDown={(e) => e.stopPropagation()}>
              <button
                type="button"
                role="menuitem"
                className="mtb-moremenu-item"
                onClick={() => { setToolsMenu(false); setGroupModal(true); }}
              >
                <Plus size={13} aria-hidden />
                {tProject('project.mytask.add_group')}
              </button>
              {/* 만든 뒤 고치거나 지우려면 여기로 — [그룹 추가] 바로 아래에 짝으로 둔다. */}
              <button
                type="button"
                role="menuitem"
                className="mtb-moremenu-item"
                onClick={() => { setToolsMenu(false); setGroupManage(true); }}
              >
                <Settings size={13} aria-hidden />
                {tProject('project.group.manage')}
              </button>
              <a role="menuitem" className="mtb-moremenu-item" href="/project/checklist-templates">
                <FileText size={13} aria-hidden />
                {tProject('project.tmpl.heading')}
              </a>
            </div>
          )}
        </div>
      </div>
      </div>

      {error && <div className="pj-error" role="alert">{error}</div>}

      {/*
        ── 승인 대기함 (2026-09-04) ──
        내가 승인해야 할 업무. 승인권이 없거나 대기 중인 것이 없으면 **아무것도 그리지 않는다**
        — 승인을 안 쓰는 조직의 화면은 종전과 똑같다.

        자리는 목록 맨 위다: 내 일보다 남을 막고 있는 일이 먼저다(내가 안 누르면 그 사람의
        업무가 끝나지 않는다). 반려는 사유를 물어야 해서 여기서는 확인창(layerConfirm)으로
        받는다 — 행마다 입력칸을 펴면 줄이 흔들린다. 자세한 사유는 상세 패널에서 쓴다.
      */}
      {pending.length > 0 && (
        <div className="mtb-apbox">
          <button
            type="button"
            className="mtb-apbox-head"
            onClick={() => setPendingOpen((v) => !v)}
            aria-expanded={pendingOpen}
          >
            <ChevronDown
              size={14}
              aria-hidden
              style={{ transform: pendingOpen ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }}
            />
            {tProject('project.mytask.ap_inbox', { n: String(pending.length) })}
            <span className="mtb-apbox-n">{pending.length}</span>
          </button>
          {pendingOpen && (
            <ul className="mtb-apbox-list">
              {pending.map((t) => (
                <li key={t.id} className="mtb-apbox-row">
                  <button
                    type="button"
                    className="mtb-apbox-title"
                    onClick={() => setDetailId(t.id)}
                  >
                    {t.title}
                  </button>
                  <span className="mtb-apbox-meta">
                    {t.project.name}
                    {t.assignees.length > 0
                      ? ` · ${tProject('project.mytask.ap_requested_by', { name: t.assignees[0]?.name ?? '' })}`
                      : ''}
                  </span>
                  <button
                    type="button"
                    className="mtb-apbtn mtb-apbtn--approve"
                    disabled={apBusyId === t.id}
                    onClick={() => void decide(t.id, 'approve')}
                    aria-label={tProject('project.mytask.ap_approve_aria', { name: t.title })}
                  >
                    {tProject('project.mytask.ap_approve')}
                  </button>
                  <button
                    type="button"
                    className="mtb-apbtn mtb-apbtn--reject"
                    disabled={apBusyId === t.id}
                    onClick={() => {
                      void (async () => {
                        // 사유를 함께 받는다 — 반려는 '왜' 가 없으면 담당자가 같은 것을 또 올린다.
                        // 취소(ESC·배경)는 null 이라 아무 일도 일어나지 않는다. 빈 문자열이면 사유 없이 반려.
                        const reason = await layerPrompt(t.title, {
                          title: tProject('project.mytask.ap_reject'),
                          confirmText: tProject('project.mytask.ap_reject_confirm'),
                          placeholder: tProject('project.mytask.ap_reject_ph'),
                          danger: true,
                        });
                        if (reason !== null) await decide(t.id, 'reject', reason);
                      })();
                    }}
                    aria-label={tProject('project.mytask.ap_reject_aria', { name: t.title })}
                  >
                    {tProject('project.mytask.ap_reject')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/*
        요약 서랍의 본문 — 완료율 도넛 · 상태 분포 · 마감 흐름 · 프로젝트 쏠림.
        내용은 종전과 같고, **기본이 접힘**이라는 것만 다르다(툴바의 [요약] 로 연다).
      */}
      {/* 2026-08-27 — 표와 요약을 나란히 두는 래퍼(추가만). 렌더 순서는 종전 그대로고
          배치만 CSS grid 좌표로 잡는다 — 요약을 접으면 표가 폭을 다 쓴다. */}
      <div className="mtb-main">
      {!dueFilter && !loading && items.length > 0 && summaryOpen && (
        /* 4분면의 점을 누르면 그 업무 상세가 열린다 — 그림에서 바로 일로 넘어가는 통로. */
        <MyTasksSummaryPanel items={items} pct={pct} onPickTask={(id) => setDetailId(id)} />
      )}

      <table className={`mtb-table${tagsOn ? ' mtb-table--tags' : ''}`}>
        <thead>
          <tr>
            {/*
              '상세' 열(매 행의 > 버튼)은 2026-08-25 에 걷어냈다 — 제목을 누르는 것과
              **완전히 같은 동작**(setDetailId)이라, 행마다 같은 버튼이 두 개씩 있던 셈이다.
            */}
            <th className="mtb-th-title">{tProject('project.mytask.col_title')}</th>
            {/*
              우선순위 열 — 2026-08-26 에 "대부분 비어 있는 열" 이라 제목 셀로 합쳤다가
              2026-09-16 에 되살렸다. 그때 비었던 이유는 '보통' 을 안 그렸기 때문이고,
              이 표의 업무는 대개 '보통' 이다(실측 8건 중 6건). 즉 열이 빈 게 아니라
              값을 숨기고 있었다.
              되살린 이유는 따로 있다 — 정렬 드롭다운에 '우선순위순' 이 있는데 정작 그 값을
              읽을 자리가 없었다. 순서가 바뀐 까닭이 화면에 없으면 정렬은 못 믿을 기능이 된다.
              그래서 이번엔 '보통' 까지 **늘 그린다**. 빈 칸이 생기지 않으니 2026-08-26 의
              걷어낸 근거도 함께 사라진다.
            */}
            <th className="mtb-th-pri">{tProject('project.mytask.col_priority')}</th>
            {/* 2026-08-27 — 폭을 채우려고 되살린 두 열. 라벨 키는 이미 있던 것을 쓴다. */}
            <th className="mtb-th-status">{tProject('project.space.col_status')}</th>
            <th className="mtb-th-prog">{tProject('project.space.col_progress')}</th>
            {/* 열이 하나 늘어 nth-child 로 잡던 폭 규칙이 어긋난다 — 클래스로 못 박는다. */}
            <th className="mtb-th-due">{tProject('project.mytask.col_due')}</th>
            {tagsOn && <th className="mtb-th-tags">{tProject('project.mytask.col_tags')}</th>}
            <th className="mtb-th-project">{tProject('project.mytask.col_project')}</th>
          </tr>
        </thead>
        <tbody>
          {/* 처음 받아 오는 동안 — 빈 표가 잠깐 "할 일이 없다" 처럼 보이던 자리. */}
          {loading && items.length === 0 && Array.from({ length: SKELETON_ROWS }, (_, i) => (
            <tr className="mtb-row mtb-row--skeleton" key={`sk-${i}`} aria-hidden>
              <td className="mtb-td-title"><Skeleton width="62%" height={14} /></td>
              <td className="mtb-td-status"><Skeleton width={56} height={14} /></td>
              <td className="mtb-td-prog"><Skeleton width="80%" height={14} /></td>
              <td><Skeleton width={72} height={14} /></td>
              {tagsOn && <td className="mtb-td-tags"><Skeleton width={60} height={14} /></td>}
              <td><Skeleton width="70%" height={14} /></td>
            </tr>
          ))}

          {!loading && openRows.length === 0 && doneRows.length === 0 && (
            <tr>
              <td colSpan={colCount} className="mtb-empty">
                {/* 필터에 안 걸린 것과 정말 없는 것을 가른다 — 전자는 해제할 길을 같이 준다. */}
                {filtered ? (
                  <span className="mtb-empty-filtered">
                    <b>{tProject('project.mytask.empty_filtered')}</b>
                    <small>{tProject('project.mytask.empty_filtered_desc')}</small>
                    <button type="button" className="mtb-clearfilters" onClick={clearFilters}>
                      {tProject('project.mytask.clear_filters')}
                    </button>
                  </span>
                ) : tProject('project.mytask.empty')}
              </td>
            </tr>
          )}

          {sections.map(({ group, rows }) => (
            <Fragment key={group?.id ?? '__none__'}>
              {useGroupHeaders && (
                <tr className="mtb-grouprow">
                  <td colSpan={colCount}>
                    {/*
                      2026-08-27 — 이 줄을 표와 **같은 grid** 로 깐다(.mtb-grow).
                      colSpan 한 칸이라 요약을 그냥 붙이면 열과 무관한 자리에 떠서, 평균
                      진척률 막대가 진척률 열이 아닌 곳에 놓였다. 같은 비율로 나누면
                      각 조각이 자기가 요약하는 열 아래에 선다.
                    */}
                    <span className="mtb-grow">
                      <span className="mtb-grow-name">
                        <span className="mtb-groupdot" style={{ background: group?.color ?? 'var(--text-muted)' }} aria-hidden />
                        {group?.name ?? tProject('project.mytask.group_none')}
                        <span className="mtb-groupcount">({rows.length})</span>
                      </span>
                      <GroupSummary rows={rows} />
                    </span>
                  </td>
                </tr>
              )}
              {rows.map(renderRow)}
            </Fragment>
          ))}

          {/*
            완료된 업무는 여기로 모은다 (2026-08-25) — 기본 목록의 절반이 취소선이던 자리.
            건수를 함께 적어, 접혀 있는 것이 '없는 것' 으로 읽히지 않게 한다.
          */}
          {doneRows.length > 0 && (
            <tr className="mtb-donerow">
              <td colSpan={colCount}>
                <button
                  type="button"
                  className={`mtb-donetoggle${showDone ? ' mtb-donetoggle--on' : ''}`}
                  aria-expanded={showDone}
                  onClick={() => setShowDone((v) => !v)}
                >
                  <ChevronDown size={14} aria-hidden />
                  {tProject(
                    showDone ? 'project.mytask.hide_done' : 'project.mytask.show_done',
                    { n: String(doneRows.length) },
                  )}
                </button>
              </td>
            </tr>
          )}
          {showDone && doneRows.map(renderRow)}

        </tbody>
      </table>
      </div>

      {loading && <p className="mtb-srstatus" role="status">{tProject('project.mytask.loading_aria')}</p>}

      {/* 100건 상한에 걸려 뒤가 잘렸다는 사실을 여기서 드러낸다 — 종전에는 조용히 잘렸다. */}
      {!loading && total > items.length && (
        <div className="mtb-more">
          <span className="mtb-more-count">
            {tProject('project.mytask.shown_of_total', { shown: String(items.length), total: String(total) })}
          </span>
          <button
            type="button"
            className="mtb-more-btn"
            onClick={() => void loadMore()}
            disabled={loadingMore}
          >
            {loadingMore ? tProject('project.mytask.loading_more') : tProject('project.mytask.load_more')}
          </button>
        </div>
      )}

      {undo && (
        <div className="mtb-undo" role="status">
          <span className="mtb-undo-msg">
            {tProject(
              undo.next === 'DONE' ? 'project.mytask.undo_marked_done' : 'project.mytask.undo_reopened',
              { name: undo.title },
            )}
          </span>
          <button type="button" className="mtb-undo-btn" onClick={() => void undoToggle()}>
            <RotateCcw size={12} aria-hidden />
            {tProject('project.mytask.undo_btn')}
          </button>
          <button
            type="button"
            className="mtb-undo-x"
            aria-label={tProject('project.mytask.undo_close')} data-tip={tProject('project.mytask.undo_close')}
            onClick={() => setUndo(null)}
          >
            <X size={12} aria-hidden />
          </button>
        </div>
      )}

      {createOpen && (
        <MyTaskCreateModal
          groups={groups}
          initialGroupId={groupFilter || null}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); void load(); }}
        />
      )}

      {groupManage && (
        <TaskGroupManageModal
          onClose={() => setGroupManage(false)}
          /* 이름이 바뀌거나 그룹이 사라지면 그룹 필터·행의 그룹 표시가 옛 값을 들고 있다. */
          onChanged={() => { void listTaskGroups().then(setGroups).catch(() => {}); }}
        />
      )}

      {groupModal && (
        <TaskGroupCreateModal
          onClose={() => setGroupModal(false)}
          onCreated={(g) => {
            setGroups((cur) => [...cur, g]);
            setGroupModal(false);
          }}
        />
      )}

      {detailTask && (
        <MyTaskDetailPanel
          taskId={detailTask.id}
          seed={{
            title: detailTask.title,
            status: detailTask.status,
            priority: detailTask.priority,
            dueDate: detailTask.dueDate,
            projectName: detailTask.project?.name ?? '',
            groupName: groupOfTask[detailTask.id]?.name ?? null,
          }}
          groups={groups}
          currentGroupId={groupOfTask[detailTask.id]?.id ?? null}
          onClose={() => setDetailId(null)}
          onChanged={() => { void load(); }}
          /* 2026-09-01 — 업무를 지우면 패널을 닫고 목록을 다시 읽는다. 닫지 않으면 이미
             없는 업무의 상세가 남고, 다시 읽지 않으면 지운 줄이 표에 그대로 남는다. */
          onDeleted={() => { setDetailId(null); void load(); }}
          onGoToProject={() => {
            const pid = detailTask.project?.id;
            navigate(pid ? `/task?project=${encodeURIComponent(pid)}` : '/task');
          }}
        />
      )}

      {/*
        기한 고르기 (2026-09-04) — 행의 [📅] 로 연다. 고르면 바로 저장하고 닫는다:
        팝오버 안에 또 [저장] 을 두면 두 번 눌러야 하는데, 여기서 정하는 값은 하나뿐이라
        고르는 것이 곧 확정이다(입력칸의 [적용] 은 직접 친 날짜를 확정하는 같은 동작이다).
      */}
      {duePick && (
        <MyTaskDuePicker
          target={duePick}
          onClose={() => setDuePick(null)}
          onPick={(next) => {
            const t = items.find((x) => x.id === duePick.id);
            setDuePick(null);
            if (t) void setDueDate(t, next);
          }}
        />
      )}
    </div>
    </ProjectNavShell>
  );
}

function ChipButton({ on, tone, label, n, onClick, icon }: {
  on: boolean; tone: string; label: string; n: number; onClick: () => void; icon?: boolean;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      className={`mtb-chip mtb-chip--${tone}${on ? ' mtb-chip--on' : ''}`}
      onClick={onClick}
    >
      {icon && <AlertTriangle size={11} aria-hidden />}
      {label} {n}
    </button>
  );
}
